import {
  buildImageCssFilter,
  buildFilteredFilename,
  type ConfirmUploadMetadata,
  type ImageFilterSettings,
} from '@omokage/shared';
import type { Photo } from '@/types/photo';

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(blob);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for filtering'));
    };

    image.src = objectUrl;
  });
}

export async function fetchImageBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch image for filtering');
  }
  return response.blob();
}

function applyCanvasEffects(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  settings: ImageFilterSettings
): void {
  const { temperature, fade, grain, vignette } = settings;

  if (temperature !== 0 || fade !== 0 || grain !== 0) {
    const imageData = context.getImageData(0, 0, width, height);
    const data = imageData.data;
    const tempShift = Math.round(temperature * 30);
    const fadeAmount = fade * 0.35;
    const grainStrength = grain * 40;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] ?? 0;
      const g = data[i + 1] ?? 0;
      const b = data[i + 2] ?? 0;

      const noise = grainStrength > 0 ? (Math.random() - 0.5) * grainStrength : 0;

      data[i] = Math.min(255, Math.max(0, r + tempShift + noise + fadeAmount * (255 - r)));
      data[i + 1] = Math.min(255, Math.max(0, g + noise + fadeAmount * (255 - g)));
      data[i + 2] = Math.min(255, Math.max(0, b - tempShift + noise + fadeAmount * (255 - b)));
    }

    context.putImageData(imageData, 0, 0);
  }

  if (vignette > 0) {
    const gradient = context.createRadialGradient(
      width / 2,
      height / 2,
      Math.min(width, height) * 0.35,
      width / 2,
      height / 2,
      Math.max(width, height) * 0.75
    );
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, `rgba(0,0,0,${vignette * 0.85})`);
    context.globalCompositeOperation = 'multiply';
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
    context.globalCompositeOperation = 'source-over';
  }
}

export async function renderFilteredImageBlob(
  sourceBlob: Blob,
  settings: ImageFilterSettings
): Promise<Blob> {
  const image = await loadImageFromBlob(sourceBlob);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to create canvas context');
  }

  if (!('filter' in context)) {
    throw new Error('Canvas filter is not supported in this browser');
  }

  context.filter = buildImageCssFilter(settings);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  context.filter = 'none';

  applyCanvasEffects(context, canvas.width, canvas.height, settings);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('Failed to export filtered image'));
      },
      sourceBlob.type || 'image/jpeg',
      sourceBlob.type === 'image/png' ? undefined : 0.92
    );
  });
}

export function buildPhotoUploadMetadata(photo: Photo): ConfirmUploadMetadata {
  return {
    takenAt: photo.takenAt ? Math.floor(new Date(photo.takenAt).getTime() / 1000) : undefined,
    worldId: photo.worldId ?? undefined,
    worldName: photo.worldName ?? undefined,
    instanceType: photo.instanceType ?? undefined,
    region: photo.region ?? undefined,
    authorName: photo.authorName ?? undefined,
    players:
      photo.friends
        ?.filter((friend) => Boolean(friend.displayName && friend.vrchatId))
        .map((friend) => ({
          displayName: friend.displayName,
          vrchatId: friend.vrchatId,
        })) ?? [],
  };
}

export function buildFilteredUploadFilename(photo: Photo): string {
  return buildFilteredFilename(photo.originalFilename || photo.filename, 'filtered');
}
